import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { EmptyState, FilterPill, StatCard } from '../components/common/ui';
import QuotesTable from '../components/quotes/QuotesTable';
import QuoteFormDialog from '../components/quotes/QuoteFormDialog';
import QuoteService from '../services/QuoteService';
import { useToast } from '../contexts/ToastContext';
import { QUOTE_STATUS, QUOTE_STATUS_ORDER } from '../styles/tokens';
import { usePageRefresh } from '../components/layout/pullToRefresh';

export default function QuotesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [searchText, setSearchText] = useState('');
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [data, setData] = useState({ items: [], total: 0 });
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        const h = setTimeout(() => { setQ(searchText); setPage(0); }, 300);
        return () => clearTimeout(h);
    }, [searchText]);

    const load = useCallback(() => {
        setLoading(true);
        return QuoteService.list({ q: q || undefined, status: status || undefined, page, size })
            .then(setData)
            .catch(() => showToast(t('common.loadError'), 'error'))
            .finally(() => setLoading(false));
    }, [q, status, page, size, showToast, t]);
    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);
    useEffect(() => { QuoteService.stats().then(s => setStats(s.byStatus)).catch(() => {}); }, []);

    const total = stats ? Object.values(stats).reduce((n, s) => n + s.count, 0) : null;
    const filtered = q || status;

    return (
        <Layout>
            <PageHeader title={t('quotes.title')} count={total || null}
                        action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>{t('quotes.new')}</Button>} />

            {stats && total > 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                    {/* Counts only: amounts are shown inside a quote, after the password. */}
                    <StatCard label={t('quotes.stats.open')} value={stats.DRAFT.count + stats.SENT.count} color={QUOTE_STATUS.SENT.color}
                              sub={t('quotes.stats.openHint')} />
                    <StatCard label={t('quotes.status.ACCEPTED')} value={stats.ACCEPTED.count} color={QUOTE_STATUS.ACCEPTED.color} />
                    <StatCard label={t('quotes.status.REJECTED')} value={stats.REJECTED.count} color={QUOTE_STATUS.REJECTED.color} />
                </Box>
            )}

            <Box sx={{ bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '14px', p: 1.5, mb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField size="small" fullWidth placeholder={t('quotes.searchPlaceholder')} value={searchText} onChange={(e) => setSearchText(e.target.value)}
                           slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment> } }} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <FilterPill label={t('common.all')} active={!status} onClick={() => { setStatus(''); setPage(0); }} count={total} />
                    {QUOTE_STATUS_ORDER.map(s => (
                        <FilterPill key={s} label={t(`quotes.status.${s}`)} color={QUOTE_STATUS[s].color} active={status === s}
                                    count={stats?.[s]?.count} onClick={() => { setStatus(status === s ? '' : s); setPage(0); }} />
                    ))}
                </Box>
            </Box>

            {loading && !data.items.length ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
            ) : !data.items.length ? (
                filtered ? <EmptyState icon={<SearchIcon />} title={t('quotes.emptyFiltered')} />
                         : <EmptyState icon={<RequestQuoteOutlinedIcon />} title={t('quotes.empty')} text={t('quotes.emptyHint')}
                                       action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>{t('quotes.new')}</Button>} />
            ) : (
                <Box sx={{ opacity: loading ? 0.6 : 1 }}>
                    <QuotesTable quotes={data.items} total={data.total} page={page} size={size}
                                 onPage={setPage} onSize={(s) => { setSize(s); setPage(0); }} />
                </Box>
            )}

            <QuoteFormDialog open={createOpen} onClose={() => setCreateOpen(false)}
                             onSaved={(quote) => { setCreateOpen(false); navigate(`/quotes/${quote.id}`); }} />
        </Layout>
    );
}
